import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Alert, Row, Col, Card } from 'react-bootstrap';
import { FaPlus, FaTrash, FaGripVertical } from 'react-icons/fa';

const QuestionSectionModal = ({ show, onHide, onSave, section, mode, activeFormType }) => {
  const [formData, setFormData] = useState({
    formType: activeFormType || '1040',
    sectionTitle: '',
    sectionOrder: 0,
    questions: [{ text: '', order: 0, isRequired: false, helpText: '' }]
  });
  const [error, setError] = useState('');

  useEffect(() => {
    if (mode === 'edit' && section) {
      setFormData({
        formType: section.formType,
        sectionTitle: section.sectionTitle,
        sectionOrder: section.sectionOrder,
        questions: section.questions.map(q => ({
          text: q.text,
          order: q.order,
          isRequired: q.isRequired,
          helpText: q.helpText || ''
        }))
      });
    } else {
      setFormData({
        formType: activeFormType || '1040',
        sectionTitle: '',
        sectionOrder: 0,
        questions: [{ text: '', order: 0, isRequired: false, helpText: '' }]
      });
    }
  }, [mode, section, activeFormType, show]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleQuestionChange = (index, field, value) => {
    const newQuestions = [...formData.questions];
    newQuestions[index][field] = value;
    setFormData(prev => ({
      ...prev,
      questions: newQuestions
    }));
  };

  const addQuestion = () => {
    setFormData(prev => ({
      ...prev,
      questions: [
        ...prev.questions,
        {
          text: '',
          order: prev.questions.length,
          isRequired: false,
          helpText: ''
        }
      ]
    }));
  };

  const removeQuestion = (index) => {
    if (formData.questions.length > 1) {
      const newQuestions = formData.questions.filter((_, i) => i !== index);
      setFormData(prev => ({
        ...prev,
        questions: newQuestions
      }));
    }
  };

  const moveQuestion = (index, direction) => {
    const newQuestions = [...formData.questions];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    
    if (targetIndex >= 0 && targetIndex < newQuestions.length) {
      [newQuestions[index], newQuestions[targetIndex]] = [newQuestions[targetIndex], newQuestions[index]];
      
      // Update order values
      newQuestions[index].order = index;
      newQuestions[targetIndex].order = targetIndex;
      
      setFormData(prev => ({
        ...prev,
        questions: newQuestions
      }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');

    if (!formData.sectionTitle.trim()) {
      setError('Section title is required');
      return;
    }

    if (formData.questions.some(q => !q.text.trim())) {
      setError('All questions must have text');
      return;
    }

    // Update question orders
    const questionsWithOrder = formData.questions.map((question, index) => ({
      ...question,
      order: index
    }));

    onSave({
      ...formData,
      questions: questionsWithOrder
    });
  };

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          {mode === 'create' ? 'Create New Section' : 'Edit Section'}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}
        
        <Form onSubmit={handleSubmit}>
          <Row className="mb-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label>Form Type</Form.Label>
                <Form.Select
                  value={formData.formType}
                  onChange={(e) => handleInputChange('formType', e.target.value)}
                  required
                >
                  <option value="1040">1040</option>
                  <option value="1065">1065</option>
                  <option value="1120-S">1120-S</option>
                  <option value="1120">1120</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Section Order</Form.Label>
                <Form.Control
                  type="number"
                  value={formData.sectionOrder}
                  onChange={(e) => handleInputChange('sectionOrder', parseInt(e.target.value))}
                  min="0"
                />
              </Form.Group>
            </Col>
          </Row>

          <Form.Group className="mb-3">
            <Form.Label>Section Title</Form.Label>
            <Form.Control
              type="text"
              value={formData.sectionTitle}
              onChange={(e) => handleInputChange('sectionTitle', e.target.value)}
              placeholder="Enter section title"
              required
            />
          </Form.Group>

          <div className="mb-3">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h5>Questions</h5>
              <Button variant="success" size="sm" onClick={addQuestion}>
                <FaPlus /> Add Question
              </Button>
            </div>

            {formData.questions.map((question, index) => (
              <Card key={index} className="mb-2">
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-start mb-2">
                    <div className="d-flex align-items-center">
                      <FaGripVertical className="text-muted me-2" />
                      <strong>Question {index + 1}</strong>
                    </div>
                    <div>
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => moveQuestion(index, 'up')}
                        disabled={index === 0}
                        className="me-1"
                      >
                        ↑
                      </Button>
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => moveQuestion(index, 'down')}
                        disabled={index === formData.questions.length - 1}
                        className="me-1"
                      >
                        ↓
                      </Button>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => removeQuestion(index)}
                        disabled={formData.questions.length === 1}
                      >
                        <FaTrash />
                      </Button>
                    </div>
                  </div>

                  <Form.Group className="mb-2">
                    <Form.Label>Question Text</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      value={question.text}
                      onChange={(e) => handleQuestionChange(index, 'text', e.target.value)}
                      placeholder="Enter question text"
                      required
                    />
                  </Form.Group>

                  <Form.Group className="mb-2">
                    <Form.Label>Help Text (Optional)</Form.Label>
                    <Form.Control
                      type="text"
                      value={question.helpText}
                      onChange={(e) => handleQuestionChange(index, 'helpText', e.target.value)}
                      placeholder="Enter help text"
                    />
                  </Form.Group>

                  <Form.Group>
                    <Form.Check
                      type="checkbox"
                      label="Required Question"
                      checked={question.isRequired}
                      onChange={(e) => handleQuestionChange(index, 'isRequired', e.target.checked)}
                    />
                  </Form.Group>
                </Card.Body>
              </Card>
            ))}
          </div>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSubmit}>
          {mode === 'create' ? 'Create Section' : 'Save Changes'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default QuestionSectionModal;